import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { brand } from '@iheart/shared';
import { MotionButton, GradientText } from '@iheart/ui';
import { api, ApiError } from '../lib/api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { PublicFooter, PublicNav } from '../components/PublicFooter';

export function ForgotPasswordRoute() {
  useDocumentTitle('Forgot Password');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/forgot-password', {
        method: 'POST',
        body: { email },
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setError(body?.message ?? 'Something went wrong. Please try again.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12 pt-20">
      <PublicNav />
      {/* Subtle gradient backdrop */}
      <div
        className="pointer-events-none absolute -left-40 -top-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -right-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />

      <div className="relative w-full max-w-sm rounded-2xl border border-white/60 bg-white/80 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl">
        <Link
          to="/login"
          className="mb-4 inline-flex items-center gap-1 text-xs text-neutral-500 transition-colors hover:text-neutral-700"
        >
          <ArrowLeft className="size-3.5" /> Back to sign in
        </Link>

        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <div
            className="flex size-12 items-center justify-center rounded-full"
            style={{ backgroundImage: brand.gradient.primary }}
          >
            <Mail className="size-6 text-white" />
          </div>
          <h1 className="text-xl font-semibold">
            <GradientText>Forgot Password</GradientText>
          </h1>
          <p className="text-sm text-neutral-500">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        {sent ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="size-6 text-emerald-500" />
            </div>
            <h2 className="text-base font-semibold text-neutral-800">Check your email</h2>
            <p className="text-sm text-neutral-500">
              If an account exists for <strong className="text-neutral-700">{email}</strong>,
              you'll receive a password reset link shortly.
            </p>
            <p className="text-xs text-neutral-400 mt-2">
              The link expires in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Link
              to="/login"
              className="mt-4 text-sm font-medium text-pink-600 transition-colors hover:text-pink-700"
            >
              Return to sign in
            </Link>
          </div>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="reset-email" className="mb-1 block text-sm font-medium text-neutral-700">
                Email address
              </label>
              <input
                id="reset-email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@iheartdigital.co.za"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                autoFocus
              />
            </div>

            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <MotionButton type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
              {submitting ? 'Sending...' : 'Send reset link'}
            </MotionButton>
          </form>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
